import React, { useState } from 'react';
import dayjs from "dayjs";
import { Link } from 'react-router-dom';
import { Box, Button, Typography, useTheme } from '@mui/material';

import DateField from 'components/DateField';
import FilterField from 'components/FilterField';
import DateRangePicker from '../../components/GraphDatePickerComponent'
import DataGridCustom from 'components/DataGridCustom';
import DataGridCustomToolbar from 'components/DataGridCustomToolbar';
import CustomModal from 'components/GetCustomModal';
import {
  useGetFilteredEnquiriesQuery,
  useDeleteEnquiryMutation,
  useGetAllAssignListQuery,
  useUpdateEnquiryMutation,
} from 'state/api';

const { useSelector } = require('react-redux');

const EnquiryTable = ({ filters, setFilters }) => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sort, setSort] = useState({ field: 'createdAt', sort: 'desc' })
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);

  const handleClose = () => setIsFilterDialogOpen(false);

  const theme = useTheme();
  const { data, isLoading } = useGetFilteredEnquiriesQuery({
    page,
    pageSize,
    sort: JSON.stringify(sort),
    search,
    ...filters,
  }); // Fetching data from the API
  const userRole = useSelector((state) => state.auth.userInfo.role);
  const [deleteEnquiry] = useDeleteEnquiryMutation();
  const [updateEnquiry] = useUpdateEnquiryMutation();

  const assignListData = useGetAllAssignListQuery();
  const assignList = assignListData?.data?.users || [];
  const handleSelectChange = async (event, id) => {
    updateEnquiry({
      id,
      assignedTo: event.target.value,
    });
  };
  // Mutation to delete enquiry
  // Function to delete enquiry
  const handleDelete = (id) => {
    const confirm = window.confirm('Are you sure you want to delete?');
    if (confirm) {
      deleteEnquiry({
        id,
      })
        .unwrap()
        .then(() => {
          alert('Enquiry deleted successfully');
        })
        .catch((err) => {
          alert('Error deleting enquiry');
        });
    }
  };

  const columns = [
    {
      field: 'customerName',
      headerName: 'Customer Name',
      width: 200,
      renderCell: (params) => {
        return (
          <Link
            to={{
              pathname: '/enquiry-view',
              search: `?id=${params.row._id}`,
            }}
            // target="_blank"
            style={{
              textDecoration: 'underline',
              fontWeight: 'bold',

              // on hover
              '&:hover': {
                color: '#1976d2 ',
                textDecoration: 'underline',
              },
              color: theme.palette.secondary[100],
            }}
          >
            <Typography
              variant='body2'
              sx={{
                fontWeight: 'bold',
                color: theme.palette.secondary[100],

                // on hover
                '&:hover': {
                  color: '#1976d2 ',
                  textDecoration: 'underline',
                },
              }}
            >
              {params.row.customerName}
            </Typography>
          </Link>
        );
      },
    },
    {
      field: 'carName',
      headerName: 'Car Name',
      width: 150,
    },
    {
      field: 'carCompany',
      headerName: 'Car Company',
      width: 100,
      hide: true,
    },
    {
      field: 'createdAt',
      headerName: 'Date Added',
      width: 200,
      renderCell: (params) => {
        return (
          <Typography variant='body2'>
            {/* Display date in Month date, year format */}
            {new Date(params.row.createdAt).toLocaleDateString('en-IN', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: 'numeric',
              minute: 'numeric',
            })}
          </Typography>
        );
      },
    },
    {
      field: 'lastRemark',
      headerName: 'Last Remark',
      width: 250,
      valueGetter: (params) => {
        return params.row.followUps.map((f) => f.remarks).join('/');
      },
      renderCell: (params) => {
        return (
          <Typography variant='body2'>
            {params.row.followUps.length > 0
              ? params.row.followUps[params.row.followUps.length - 1].remarks
              : '-'}
          </Typography>
        );
      },
    },
    {
      field: 'nextFollowUpDate',
      headerName: 'Next Follow Up Date',
      width: 200,

      renderCell: (params) => {
        return (
          <Typography variant='body2'>
            {/* Display date in Month date, year format */}
            {new Date(params.row.nextFollowUpDate).toLocaleDateString('en-IN', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: 'numeric',
              minute: 'numeric',
            })}
          </Typography>
        );
      },
    },
    {
      field: 'enquiryStatus',
      headerName: 'Status',
      width: 100,

      // render cell with status color
      renderCell: (params) => {
        return (
          <Typography
            variant='body2'
            sx={{
              color:
                params.row.enquiryStatus === 'Ongoing'
                  ? theme.palette.success.main
                  : theme.palette.error.main,
            }}
          >
            {params.row.enquiryStatus}
          </Typography>
        );
      },
    },

    {
      field: 'followUps',
      headerName: 'Follow Ups Till Now',
      width: 100,
      valueGetter: (params) => params.row.followUps.length,
      renderCell: (params) => {
        return (
          <Typography variant='body2'>{params.row.followUps.length}</Typography>
        );
      },

      sortable: false,
    },
    {
      field: 'leadSource',
      headerName: 'Lead Source',
      width: 150,
      hide: true,
    },
    {
      field: 'customerNumber',
      headerName: 'Phone',
      width: 150,
    },
    {
      field: 'assignee',
      headerName: 'Assignee',
      width: 150,
      hide: userRole === 'Sales Consultant',
      valueGetter: (params) => {
        const result = assignList.find(
          (item) => item._id === params.row.assignedTo
        );
        return result ? result.name : '';
      },
      renderCell: (params) => {
        const defaultSelected = assignList.find(
          (item) => item._id === params.row.assignedTo
        );
        return (
          <>
            {assignListData.isLoading ? (
              <div>Loading...</div>
            ) : (
              <select
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderBottom: '1px solid #333',
                  padding: '10px',
                  fontSize: '16px',
                  fontFamily: 'Arial, sans-serif',
                  color: '#333',
                  width: '150px',
                  cursor: 'pointer',
                  outline: 'none',
                }}
                name='assignedTo'
                id='assignedTo'
                onChange={(e) => handleSelectChange(e, params.row._id)}
                defaultValue={defaultSelected?._id}
              >
                <option value='' disabled selected>
                  Select
                </option>
                {assignList.map(({ _id, name }) => (
                  <option key={_id} value={_id}>
                    {name}
                  </option>
                ))}
              </select>
            )}
          </>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      valueGetter: () => undefined,
      renderCell: (params) => {
        return (
          <Button
            variant='outlined'
            sx={{
              color: theme.palette.error.main,
              cursor: 'pointer',
              borderColor: theme.palette.error.main,
            }}
            onClick={() => handleDelete(params.row._id)}
          >
            Delete
          </Button>
        );
      },
    },
    {
      field: 'customerEmail',
      headerName: 'Email',
      width: 250,
      hide: true,
    },
    {
      field: 'customerCity',
      headerName: 'City',
      width: 150,
      hide: true,
    },
  ];

  return (
    <>
      <Box mt='40px' width='100%' height='75vh'>
        <DataGridCustom
          disableMultipleColumnsFiltering={false}
          loading={isLoading || !data}
          getRowId={(row) => row._id}
          rows={data?.result ?? []}
          columns={
            // align header to center
            columns.map((column) => ({
              ...column,
              headerAlign: 'center',
              align: 'center',
            }))
          }
          disableSelectionOnClick
          rowCount={data?.total ?? 0}
          rowsPerPageOptions={[10, 20, 50, 100]}
          pagination
          pageSize={pageSize}
          paginationMode='server'
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          onSortModelChange={(model) =>  setSort(...model)}
          components={{ Toolbar: DataGridCustomToolbar }}
          componentsProps={{
            toolbar: {
              searchInput,
              setSearchInput,
              setSearch,
              setIsFilterDialogOpen,
              showFilter: true,
              userRole,
              exportType: 'enquiries',
            },
          }}
          rowHeight={80}
          autoHeight
        />
      </Box>
      {isFilterDialogOpen && (
        <CustomModal
          open={isFilterDialogOpen}
          handleClose={handleClose}
          title='Filters'
          sx={{ textAlign: 'center' }}
        >
          <div className='flex flex-wrap items-center justify-center'>
            <DateRangePicker
              width={250}
              startDate={dayjs(new Date(parseInt(filters.from ?? 1704047400000 /* 01 Jan 2024 */)))}
              endDate={dayjs(new Date(parseInt(filters.to ?? Date.now())))}
              handleStartDateChange={(value) => setFilters(fs => ({
                ...fs,
                from: Date.parse(value.startOf()).toString()
              }))}
              handleEndDateChange={(value) => setFilters(fs => ({
                ...fs,
                to: Date.parse(value.startOf()).toString()
              }))}
            />
            <FilterField
              labels={(id) => data?.userMap[id]}
              options={data?.options}
              filters={filters}
              setFilters={setFilters}
              label='Assignee'
              name='assignedTo'
            />
            <DateField
              label='Pending Followup'
              name='pending'
              value={dayjs(new Date(parseInt(filters.pendingUntill ?? Date.now())))}
              onChange={(value) => setFilters(fs => ({
                ...fs,
                pending: undefined,
                pendingUntill: Date.parse(value).toString()
              }))}
            />
            <FilterField
              options={data?.options}
              filters={filters}
              setFilters={setFilters}
              label='Car Company'
              name='carCompany'
            />
            <FilterField
              options={data?.options}
              filters={filters}
              setFilters={setFilters}
              label='Car Name'
              name='carName'
            />
            <FilterField
              options={data?.options}
              filters={filters}
              setFilters={setFilters}
              label='Enquiry Status'
              name='enquiryStatus'
            />
            <FilterField
              options={data?.options}
              filters={filters}
              setFilters={setFilters}
              label='Lead Status'
              name='leadStatus'
            />
            <FilterField
              options={data?.options}
              filters={filters}
              setFilters={setFilters}
              label='Customer City'
              name='customerCity'
            />
            <FilterField
              options={data?.options}
              filters={filters}
              setFilters={setFilters}
              label='Lead Source'
              name='leadSource'
            />
          </div>
        </CustomModal>
      )}
    </>
  );
};

export default EnquiryTable;
